import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Target,
  Loader2, 
  RefreshCw, 
  ChevronRight,
  ChevronLeft,
  Mail,
  MapPin,
  Calendar,
  MessageSquare,
  FileText,
  Plus,
  ClipboardList
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { calculatorApi } from '../services/api';
import CreateSurveyFromEstimate from './SurveyManagement/CreateSurveyFromEstimate';

const PipelineView = () => {
  const [entries, setEntries] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stageFilter, setStageFilter] = useState('all');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [updatingStage, setUpdatingStage] = useState(null);
  const [stageNotes, setStageNotes] = useState('');
  const [showCreateQuoteModal, setShowCreateQuoteModal] = useState(false);
  const [selectedEntryForQuote, setSelectedEntryForQuote] = useState(null);
  const [showCreateSurveyModal, setShowCreateSurveyModal] = useState(false);
  const [selectedEntryForSurvey, setSelectedEntryForSurvey] = useState(null);

  useEffect(() => {
    loadPipelineEntries();
  }, [stageFilter]);

  const loadPipelineEntries = async () => {
    setLoading(true);
    try {
      const response = await calculatorApi.getPipelineEntries(stageFilter, 100);
      setEntries(response.data.entries);
      setMetrics(response.data.metrics);
      setStages(response.data.stages);
    } catch (error) {
      console.error('Error loading pipeline entries:', error);
      toast({
        title: "Error",
        description: "Failed to load pipeline entries",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStageUpdate = async (entryId, newStage) => {
    setUpdatingStage(entryId);
    try {
      await calculatorApi.updatePipelineStage(entryId, newStage, stageNotes || null);
      
      // Update local state
      setEntries(prev => prev.map(entry => 
        entry.id === entryId 
          ? { ...entry, stage: newStage, stage_updated_at: new Date().toISOString() }
          : entry
      ));
      
      const stageInfo = stages.find(s => s.key === newStage);
      toast({
        title: "Stage Updated",
        description: `Moved to: ${stageInfo?.label || newStage}`,
      });
      
      setShowStageModal(false);
      setSelectedEntry(null);
      setStageNotes('');
      
    } catch (error) {
      console.error('Error updating stage:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update pipeline stage",
        variant: "destructive",
      });
    } finally {
      setUpdatingStage(null);
    }
  };

  const openStageModal = (entry) => {
    setSelectedEntry(entry);
    setStageNotes('');
    setShowStageModal(true);
  };

  const getStageColor = (stageKey) => {
    const stage = stages.find(s => s.key === stageKey);
    const colorMap = {
      'blue': 'bg-blue-100 text-blue-800',
      'yellow': 'bg-yellow-100 text-yellow-800',
      'purple': 'bg-purple-100 text-purple-800',
      'orange': 'bg-orange-100 text-orange-800',
      'indigo': 'bg-indigo-100 text-indigo-800',
      'green': 'bg-green-100 text-green-800',
      'gray': 'bg-gray-100 text-gray-800'
    };
    return colorMap[stage?.color] || 'bg-gray-100 text-gray-800';
  };

  const getStageLabel = (stageKey) => {
    const stage = stages.find(s => s.key === stageKey);
    return stage?.label || stageKey;
  };

  const openCreateQuoteModal = (entry) => {
    setSelectedEntryForQuote(entry);
    setShowCreateQuoteModal(true);
  };

  const openCreateSurveyModal = (entry) => {
    setSelectedEntryForSurvey(entry);
    setShowCreateSurveyModal(true);
  };

  const createQuoteFromEstimate = async (entry) => {
    try {
      // Convert estimate windows to quote format
      const quoteWindows = entry.estimate?.windows?.map((window, index) => ({
        id: window.id || index + 1,
        label: window.label || `Window ${index + 1}`,
        width: window.width,
        height: window.height,
        unit: window.unit,
        type: window.type,
        area: window.area || ((window.width / (window.unit === 'mm' ? 1000 : 100)) * (window.height / (window.unit === 'mm' ? 1000 : 100))),
        cost: window.cost,
        price_per_sqm: window.price_per_sqm,
        // Default quote-specific fields that staff can modify
        shutter_colour: 'White',
        louvre_size: '63mm',
        control_type: 'Central Tilt Rod',
        hinge_colour: 'White',
        discount_percentage: 0
      })) || [];

      // Prepare quote data
      const quoteData = {
        user_email: entry.customer_email,
        customer_name: entry.customer_name,
        customer_postcode: entry.customer_postcode || '',
        windows: quoteWindows,
        overall_discount_percentage: 0,
        notes: `Quote created from estimate #${entry.id}. Original estimate: £${entry.estimate?.total_cost}`,
        valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days from now
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/quotes/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteData)
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Quote Created Successfully",
          description: `Quote ${data.data.quote_number} created for ${entry.customer_name}. It's ready to customize and send.`
        });

        // Update the estimate stage to "Quote Confirmed"
        await handleStageUpdate(entry.id, 'quote_confirmed');
        
        setShowCreateQuoteModal(false);
        setSelectedEntryForQuote(null);
      } else {
        throw new Error(data.data?.error || 'Failed to create quote');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Quote Creation Failed",
        description: `Failed to create quote: ${error.message}`
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNextStage = (currentStage) => {
    const currentIndex = stages.findIndex(s => s.key === currentStage);
    return currentIndex < stages.length - 1 ? stages[currentIndex + 1] : null;
  };

  const getPreviousStage = (currentStage) => {
    const currentIndex = stages.findIndex(s => s.key === currentStage);
    return currentIndex > 0 ? stages[currentIndex - 1] : null;
  };

  return (
    <div className="space-y-6">
      {/* Pipeline Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Entries */}
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Estimates</p>
                <p className="text-3xl font-bold text-gray-900">{metrics.total_entries || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                In pipeline management
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Total Pipeline Value */}
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pipeline Value</p>
                <p className="text-3xl font-bold text-green-600">£{(metrics.total_value || 0).toFixed(2)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Avg: £{(metrics.average_value || 0).toFixed(2)} per estimate
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Active Stage Count */}
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-3xl font-bold text-orange-600">
                  {(metrics.stage_counts?.deposit_paid_in_production || 0) + 
                   (metrics.stage_counts?.installation_scheduled || 0)}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Target className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Production + Installation
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Completed */}
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-3xl font-bold text-green-600">
                  {(metrics.stage_counts?.completed_send_review || 0) + 
                   (metrics.stage_counts?.completed_no_review || 0)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-600">
                Value: £{((metrics.stage_values?.completed_send_review || 0) + 
                          (metrics.stage_values?.completed_no_review || 0)).toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Filter by stage:</label>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {stages.map(stage => (
                  <SelectItem key={stage.key} value={stage.key}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-gray-600">
            {entries.length} estimate{entries.length !== 1 ? 's' : ''}
          </div>
        </div>
        
        <Button 
          onClick={loadPipelineEntries} 
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {/* Pipeline Entries */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-lg text-gray-600">Loading pipeline...</p>
        </div>
      ) : entries.length === 0 ? (
        <Card className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
          <CardContent className="text-center py-12">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-600">No estimates in pipeline</p>
            <p className="text-sm text-gray-500 mt-2">
              Estimates will appear here when customers submit them
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {entries.map((entry) => (
            <Card key={entry.id} className="shadow-lg border-0 bg-white/70 backdrop-blur-sm">
              <CardHeader className="pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg text-gray-900 mb-2">
                      {entry.customer_name}
                    </CardTitle>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getStageColor(entry.stage)}>
                        {getStageLabel(entry.stage)}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        Updated: {formatDate(entry.stage_updated_at)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-blue-600">
                      £{entry.total_cost?.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {entry.windows_count} window{entry.windows_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Customer Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{entry.customer_email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{entry.customer_postcode}</span>
                  </div>
                </div>

                {/* Notes */}
                {entry.notes && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <MessageSquare className="h-4 w-4 text-gray-500" />
                      <h4 className="text-sm font-medium text-gray-700">Notes:</h4>
                    </div>
                    <p className="text-sm text-gray-600">{entry.notes}</p>
                  </div>
                )}

                <Separator />

                {/* Stage Controls */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Move to stage:</span>
                    <div className="flex gap-2">
                      {getPreviousStage(entry.stage) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStageUpdate(entry.id, getPreviousStage(entry.stage).key)}
                          disabled={updatingStage === entry.id}
                          className="text-xs flex items-center gap-1"
                        >
                          <ChevronLeft className="h-3 w-3" />
                          {getPreviousStage(entry.stage).label}
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openStageModal(entry)}
                        disabled={updatingStage === entry.id}
                        className="text-xs"
                      >
                        Update Stage
                      </Button>
                      
                      {getNextStage(entry.stage) && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleStageUpdate(entry.id, getNextStage(entry.stage).key)}
                          disabled={updatingStage === entry.id}
                          className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-700"
                        >
                          {updatingStage === entry.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              {getNextStage(entry.stage).label}
                              <ChevronRight className="h-3 w-3" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Create Quote Button */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => openCreateSurveyModal(entry)}
                      className="bg-[#2F4F4F] hover:bg-[#3d6363] text-white text-xs flex items-center gap-1"
                    >
                      <ClipboardList className="h-3 w-3" />
                      Create Survey
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openCreateQuoteModal(entry)}
                      style={{backgroundColor: '#8CB89F'}}
                      className="hover:opacity-90 text-white text-xs flex items-center gap-1"
                    >
                      <FileText className="h-3 w-3" />
                      Create Quote
                    </Button>
                    <div className="text-xs text-gray-500">
                      Created: {formatDate(entry.created_at)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Stage Update Modal */}
      <Dialog open={showStageModal} onOpenChange={setShowStageModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Update Pipeline Stage</DialogTitle>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-medium">{selectedEntry.customer_name}</h4>
                <p className="text-sm text-gray-600">Current stage: {getStageLabel(selectedEntry.stage)}</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Select new stage:</label>
                <div className="grid grid-cols-1 gap-2">
                  {stages.map(stage => (
                    <Button
                      key={stage.key}
                      variant={stage.key === selectedEntry.stage ? "default" : "outline"}
                      onClick={() => handleStageUpdate(selectedEntry.id, stage.key)}
                      disabled={updatingStage === selectedEntry.id}
                      className="justify-start text-sm"
                    >
                      {updatingStage === selectedEntry.id ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      {stage.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes (optional):</label>
                <Textarea
                  value={stageNotes}
                  onChange={(e) => setStageNotes(e.target.value)}
                  placeholder="Add notes about this stage update..."
                  rows={3}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Quote Confirmation Modal */}
      <Dialog open={showCreateQuoteModal} onOpenChange={setShowCreateQuoteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Quote from Estimate</DialogTitle>
          </DialogHeader>
          
          {selectedEntryForQuote && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Customer Details</h4>
                <div className="space-y-1 text-sm">
                  <p><strong>Name:</strong> {selectedEntryForQuote.customer_name}</p>
                  <p><strong>Email:</strong> {selectedEntryForQuote.customer_email}</p>
                  <p><strong>Postcode:</strong> {selectedEntryForQuote.customer_postcode}</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">Estimate Details</h4>
                <div className="space-y-1 text-sm">
                  <p><strong>Windows:</strong> {selectedEntryForQuote.windows_count}</p>
                  <p><strong>Total Cost:</strong> £{selectedEntryForQuote.total_cost?.toFixed(2)}</p>
                  <p><strong>Current Stage:</strong> {getStageLabel(selectedEntryForQuote.stage)}</p>
                </div>
              </div>
              
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-900 mb-2">Quote Creation</h4>
                <div className="text-sm text-amber-800">
                  <p>• A draft quote will be created with default shutter specifications</p>
                  <p>• Staff can customize colors, louvre sizes, and controls before sending</p>
                  <p>• The estimate will be moved to "Quote Confirmed" stage</p>
                  <p>• Quote will be valid for 30 days</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <div className="flex gap-2 w-full">
              <Button 
                variant="outline" 
                onClick={() => setShowCreateQuoteModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => createQuoteFromEstimate(selectedEntryForQuote)}
                style={{backgroundColor: '#8CB89F'}}
                className="hover:opacity-90 text-white flex-1"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Quote
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Survey from Estimate Modal */}
      <CreateSurveyFromEstimate 
        estimate={selectedEntryForSurvey}
        open={showCreateSurveyModal}
        onOpenChange={setShowCreateSurveyModal}
        onCreated={() => {
          loadPipelineEntries();
          toast({
            title: "Survey Created",
            description: "You can now view and manage the survey in the Surveys tab"
          });
        }}
      />
    </div>
  );
};

export default PipelineView;